var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AUDIO_ONLY_SCREEN_SHARE_NO_TRACK } from '../../../../modules/UI/UIErrors';
import { showModeratedNotification } from '../../av-moderation/actions';
import { shouldShowModeratedNotification } from '../../av-moderation/functions';
import { setNoiseSuppressionEnabled } from '../../noise-suppression/actions';
import { showNotification } from '../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../notifications/constants';
import { setScreenAudioShareState, setScreenshareAudioTrack } from '../../screen-share/actions';
import { isAudioOnlySharing } from '../../screen-share/functions';
import { AudioMixerEffect } from '../../stream-effects/audio-mixer/AudioMixerEffect';
import { getCurrentConference } from '../conference/functions';
import { MHTrackErrors, MHTrackEvents } from '../lib-meet-hour';
import { gumPending } from '../media';
import { setScreenshareMuted } from '../media/actions';
import { MEDIA_TYPE, VIDEO_TYPE } from '../media/constants';
import { IGUMPendingState } from '../media/types';
import { addLocalTrack, replaceLocalTrack } from './actions';
import { createLocalTracksF, getLocalDesktopTrack, getLocalMHAudioTrack, isScreenVideoShared } from './functions';
import logger from './logger';
/**
 * Signals that the local participant is ending screensharing or beginning the screensharing flow.
 *
 * @param {boolean} enabled - The state to toggle screen sharing to.
 * @param {boolean} audioOnly - Only share system audio.
 * @param {Object} shareOptions - The options to be passed for capturing screenshare.
 * @returns {Function}
 */
export function toggleScreensharing(enabled, audioOnly = false, shareOptions = {}) {
    return (dispatch, getState) => {
        // check for A/V Moderation when trying to start screen sharing
        if ((enabled || enabled === undefined) && shouldShowModeratedNotification(MEDIA_TYPE.VIDEO, getState())) {
            dispatch(showModeratedNotification(MEDIA_TYPE.SCREENSHARE));
            return Promise.reject();
        }
        return _toggleScreenSharing({
            enabled,
            audioOnly,
            shareOptions
        }, {
            dispatch,
            getState
        });
    };
}
/**
 * Displays a UI notification for screensharing failure based on the error passed.
 *
 * @private
 * @param {Object} error - The error.
 * @param {Object} store - The redux store.
 * @returns {void}
 */
function _handleScreensharingError(error, { dispatch }) {
    if (error.name === MHTrackErrors.SCREENSHARING_USER_CANCELED) {
        return;
    }
    let descriptionKey, titleKey;
    if (error.name === MHTrackErrors.PERMISSION_DENIED) {
        descriptionKey = 'dialog.screenSharingPermissionDeniedError';
        titleKey = 'dialog.screenSharingFailedTitle';
    }
    else if (error.name === MHTrackErrors.CONSTRAINT_FAILED) {
        descriptionKey = 'dialog.cameraConstraintFailedError';
        titleKey = 'deviceError.cameraError';
    }
    else if (error.name === MHTrackErrors.SCREENSHARING_GENERIC_ERROR) {
        descriptionKey = 'dialog.screenSharingFailed';
        titleKey = 'dialog.screenSharingFailedTitle';
    }
    else if (error === AUDIO_ONLY_SCREEN_SHARE_NO_TRACK) {
        descriptionKey = 'notify.screenShareNoAudio';
        titleKey = 'notify.screenShareNoAudioTitle';
    }
    dispatch(showNotification({
        titleKey,
        descriptionKey
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
/**
 * Applies the AudioMixer effect on the local audio track if applicable. If there is no local audio track, the desktop
 * audio track is added to the conference.
 *
 * @private
 * @param {MHLocalTrack} desktopAudioTrack - The audio track to be added to the conference.
 * @param {*} state - The redux state.
 * @returns {void}
 */
function _maybeApplyAudioMixerEffect(desktopAudioTrack, state) {
    return __awaiter(this, void 0, void 0, function* () {
        const localAudio = getLocalMHAudioTrack(state);
        const conference = getCurrentConference(state);
        if (localAudio) {
            // If there is a localAudio stream, mix in the desktop audio stream captured by the screen sharing API.
            const mixerEffect = new AudioMixerEffect(desktopAudioTrack);
            yield localAudio.setEffect(mixerEffect);
        }
        else {
            // If no local stream is present ( i.e. no input audio devices) we use the screen share audio
            // stream as we would use a regular stream.
            yield (conference === null || conference === void 0 ? void 0 : conference.replaceTrack(null, desktopAudioTrack));
        }
    });
}
/**
 * Toggles screen sharing.
 *
 * @private
 * @param {boolean} enabled - The state to toggle screen sharing to.
 * @param {Store} store - The redux store.
 * @returns {void}
 */
function _toggleScreenSharing(_a, store_1) {
    return __awaiter(this, arguments, void 0, function* ({ enabled, audioOnly = false, shareOptions = {} }, store) {
        const { dispatch, getState } = store;
        const state = getState();
        const audioOnlySharing = isAudioOnlySharing(state);
        const screenSharing = isScreenVideoShared(state);
        const conference = getCurrentConference(state);
        const localAudio = getLocalMHAudioTrack(state);
        const localScreenshare = getLocalDesktopTrack(state['features/base/tracks']);
        // Toggle screenshare or audio-only share if the new state is not passed. Happens in the following two cases.
        // 1. ShareAudioDialog passes undefined when the user hits continue in the share audio demo modal.
        // 2. Toggle screenshare called from the external API.
        const enable = audioOnly
            ? enabled !== null && enabled !== void 0 ? enabled : !audioOnlySharing
            : enabled !== null && enabled !== void 0 ? enabled : !screenSharing;
        const screensharingDetails = {};
        if (enable) {
            let tracks;
            // Spot proxy stream.
            if (shareOptions.desktopStream) {
                tracks = [shareOptions.desktopStream];
            }
            else {
                const { _desktopSharingSourceDevice } = state['features/base/config'];
                if (!shareOptions.desktopSharingSources && _desktopSharingSourceDevice) {
                    shareOptions.desktopSharingSourceDevice = _desktopSharingSourceDevice;
                }
                const options = Object.assign({ devices: [VIDEO_TYPE.DESKTOP] }, shareOptions);
                try {
                    tracks = yield createLocalTracksF(options);
                }
                catch (error) {
                    _handleScreensharingError(error, store);
                    if (error.name !== MHTrackErrors.SCREENSHARING_USER_CANCELED) {
                        throw error;
                    }
                    return;
                }
            }
            const desktopAudioTrack = tracks.find(track => track.getType() === MEDIA_TYPE.AUDIO);
            const desktopVideoTrack = tracks.find(track => track.getType() === MEDIA_TYPE.VIDEO);
            if (audioOnly) {
                // Dispose the desktop track for audio-only screensharing.
                desktopVideoTrack.dispose();
                if (!desktopAudioTrack) {
                    _handleScreensharingError(AUDIO_ONLY_SCREEN_SHARE_NO_TRACK, store);
                    return;
                }
            }
            else if (desktopVideoTrack) {
                // console.log('Screenshare track obtained');
                if (localScreenshare) {
                    yield dispatch(replaceLocalTrack(localScreenshare.mhTrack, desktopVideoTrack, conference));
                }
                else {
                    yield dispatch(addLocalTrack(desktopVideoTrack));
                }
                // if (isScreenshotCaptureEnabled(state, false, true)) {
                // dispatch(toggleScreenshotCaptureSummary(true));
                // }
                screensharingDetails.sourceType = desktopVideoTrack.sourceType;
            }
            // Apply the AudioMixer effect if there is a local audio track, add the desktop track to the conference
            // otherwise without unmuting the microphone.
            if (desktopAudioTrack) {
                // Noise suppression doesn't work with desktop audio because we can't chain track effects yet, disable it
                // first. We need to to wait for the effect to clear first or it might interfere with the audio mixer.
                yield dispatch(setNoiseSuppressionEnabled(false));
                _maybeApplyAudioMixerEffect(desktopAudioTrack, state);
                dispatch(setScreenshareAudioTrack(desktopAudioTrack));
                // Handle the case where screen share was stopped from the browsers 'screen share in progress' window.
                if (audioOnly) {
                    desktopAudioTrack === null || desktopAudioTrack === void 0 ? void 0 : desktopAudioTrack.on(MHTrackEvents.LOCAL_TRACK_STOPPED, () => dispatch(toggleScreensharing(undefined, true)));
                }
            }
            // Show notification about more bandwidth usage in audio-only mode if the user starts screensharing. This
            // doesn't apply to audio-only screensharing.
            const { enabled: bestPerformanceMode } = state['features/base/audio-only'];
            if (bestPerformanceMode && !audioOnly) {
                dispatch(showNotification({
                    titleKey: 'notify.screenSharingAudioOnlyTitle',
                    descriptionKey: 'notify.screenSharingAudioOnlyDescription'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }
        }
        else {
            const { desktopAudioTrack } = state['features/screen-share'];
            const { stopReceiver } = yield import('../../remote-control/actions');
            dispatch(stopReceiver());
            // dispatch(toggleScreenshotCaptureSummary(false));
            // Mute the desktop track instead of removing it from the conference since we don't want the client to signal
            // a source-remove to the remote peer for the screenshare track. Later when screenshare is enabled again, the
            // same sender will be re-used without the need for signaling a new ssrc through source-add.
            dispatch(setScreenshareMuted(true));
            if (desktopAudioTrack) {
                if (localAudio) {
                    // Await it. setEffect() renegotiates the peerconnection before it unwinds the mixer, so leaving it
                    // unawaited raced the dispose() below: the desktop track the mixer is still reading from could be
                    // stopped mid-teardown, and a rejection here (the track can be mid-renegotiation) meant the mixer
                    // never reset at all - leaving the microphone routed through a dead mixer whose AudioContext was
                    // never released.
                    try {
                        yield localAudio.setEffect(undefined);
                    }
                    catch (error) {
                        logger.error('Failed to remove the audio mixer effect on screenshare stop', error);
                    }
                }
                else {
                    yield (conference === null || conference === void 0 ? void 0 : conference.replaceTrack(desktopAudioTrack, null));
                }
                desktopAudioTrack.dispose();
                dispatch(setScreenshareAudioTrack(null));
            }
        }
        if (audioOnly) {
            dispatch(setScreenAudioShareState(enable));
        }
        else {
            // Notify the external API.
            APP.API.notifyScreenSharingStatusChanged(enable, screensharingDetails);
        }
    });
}
/**
 * Creates and adds to the conference the initial audio/video tracks.
 *
 * @param {Array<MediaType>} devices - Array with devices (audio/video) that will be used.
 * @returns {Function}
 */
export function createAndAddInitialAVTracks(devices) {
    return (dispatch) => __awaiter(this, void 0, void 0, function* () {
        dispatch(gumPending(devices, IGUMPendingState.PENDING_UNMUTE));
        const { tracks, errors } = yield dispatch(createInitialAVTracks({ devices }));
        setGUMPendingStateOnFailedTracks(tracks, dispatch);
        dispatch(displayErrorsForCreateInitialLocalTracks(errors));
        yield Promise.allSettled(tracks.map((track) => {
            const legacyConferenceObject = APP.conference;
            if (track.isAudioTrack()) {
                return legacyConferenceObject.useAudioStream(track);
            }
            if (track.isVideoTrack()) {
                return legacyConferenceObject.useVideoStream(track);
            }
            return Promise.resolve();
        }));
        dispatch(gumPending(devices, IGUMPendingState.NONE));
    });
}
